/**
 * Kiosk pairing form ("I have my camera" - Option 2 flow).
 *
 * Org / site come from the URL query string (set by the Checkouts page when
 * launching the kiosk and forwarded by CameraCheckoutPage). We no longer
 * render org/site dropdowns here - the kiosk already knows the tenant from
 * the launch context, identical to the "I need a camera" flow.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Combobox from './Combobox';
import PinInput, { FieldError } from './PinInput';
import { fetchEmployees, pairUser } from './api';
import { InvalidPinError } from './errors';

/** Resolve a public-folder asset URL respecting Vite's BASE_URL. */
const ICON_BASE =
  `${(import.meta.env.BASE_URL || '/').replace(/\/$/, '')}/icons/camera-checkout-page`;

export default function PairingPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const checkoutId = params.get('checkoutId') || '';
  const orgName    = params.get('org')    || '';
  const siteName   = params.get('site')   || '';
  const orgId      = params.get('orgId')  || '';
  const siteId     = params.get('siteId') || '';

  const [userId, setUserId] = useState(null);
  const [comboText, setComboText] = useState('');
  const [pin, setPin]       = useState('');

  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  const [submitting, setSubmitting]     = useState(false);
  const [pinError, setPinError]         = useState(null);
  const [generalError, setGeneralError] = useState(null);

  useEffect(() => {
    if (!orgId || !siteId) { setEmployees([]); return; }
    setLoadingEmployees(true);
    fetchEmployees(orgId, siteId)
      .then(setEmployees)
      .catch((e) => setGeneralError(e.message ?? 'Failed to load employees'))
      .finally(() => setLoadingEmployees(false));
  }, [orgId, siteId]);

  const employeeOptions = useMemo(
    () => employees.map((e) => ({ value: e.id, label: e.name, searchText: e.employeeId })),
    [employees]
  );

  const canSubmit = !!orgId && !!siteId && (!!userId || comboText.trim().length > 0) && pin.length === 4 && !submitting;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setPinError(null);
    setGeneralError(null);
    try {
      let emp = employees.find((x) => x.id === userId);
      if (!emp && comboText.trim()) {
        const text = comboText.trim().toLowerCase();
        emp = employees.find((e) =>
          e.name.toLowerCase() === text ||
          e.employeeId.toLowerCase() === text
        );
      }
      if (!emp) {
        setGeneralError('Please select a valid user from the list.');
        setSubmitting(false);
        return;
      }

      const res = await pairUser({
        orgId,
        siteId,
        userId: emp.id,
        pin,
      });
      navigate(`/checkout/success${buildQs()}`, {
        state: {
          name: emp.name,
          employeeId: emp.employeeId ?? '',
          deviceId: res.deviceId,
        },
      });
    } catch (err) {
      if (err instanceof InvalidPinError) {
        setPinError('Invalid code. Please try again.');
      } else {
        setGeneralError(err.message ?? 'Pairing failed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  const buildQs = () => {
    const qp = new URLSearchParams();
    if (checkoutId) qp.set('checkoutId', checkoutId);
    if (orgName)  qp.set('org',    orgName);
    if (siteName) qp.set('site',   siteName);
    if (orgId)    qp.set('orgId',  orgId);
    if (siteId)   qp.set('siteId', siteId);
    const s = qp.toString();
    return s ? `?${s}` : '';
  };

  const handleBack = () => {
    navigate(`/checkout/camera${buildQs()}`);
  };

  return (
    <div className="ck-page">
      <header className="ck-header">
        <div className="nc-header-left">
          <button type="button" className="nc-back-btn" title="Back" onClick={handleBack}>
            <i className="fas fa-arrow-left"></i>
          </button>
          <div className="cc-brand">
            {orgName || 'Organization'}
            {siteName && <span className="cc-brand-site">/{siteName}</span>}
          </div>
        </div>

        <div className="cc-header-actions">
          <button
            type="button"
            className="ck-icon-btn"
            title="Exit"
            onClick={() => navigate('/checkout')}
          >
            <img src={`${ICON_BASE}/signout.svg`} alt="" className="ck-icon-btn-img" />
          </button>
          <button type="button" aria-label="Help" className="ck-icon-btn">
            <img src={`${ICON_BASE}/questionmark.svg`} alt="" className="ck-icon-btn-img" />
          </button>
          <div className="ck-language-chip">
            <span>English</span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
      </header>

      <main className="ck-main">
        <div className="ck-card">
          <h1 className="ck-card-title">Sign in and enter code</h1>
          <p className="ck-card-subtitle">
            Sign in and enter the 4-digit code to link your camera
          </p>

          <div className="ck-device-box">
            <img
              src={`${import.meta.env.BASE_URL}icons/camera-checkout-page/has-camera-guide.png`}
              alt="Motorola device showing 4-digit code entry screen"
              className="ck-device-img"
              draggable={false}
            />
          </div>

          <form onSubmit={handleSubmit} noValidate className="ck-form">
            <div className="ck-field">
              <label htmlFor="employee" className="ck-label">
                Name/Employee ID<span className="ck-required">*</span>
              </label>
              <Combobox
                id="employee"
                options={employeeOptions}
                value={userId}
                onChange={(v) => { setUserId(v); setPinError(null); }}
                onInputChange={setComboText}
                placeholder={
                  !orgId || !siteId
                    ? 'Relaunch from Checkouts to select a site'
                    : 'Search your name or ID'
                }
                disabled={!orgId || !siteId}
                loading={loadingEmployees}
              />
            </div>

            <div className="ck-field">
              <label htmlFor="pin" className="ck-label">
                4-digit code<span className="ck-required">*</span>
              </label>
              <PinInput id="pin" value={pin} onChange={(v) => { setPin(v); if (pinError) setPinError(null); }} invalid={!!pinError} />
              {pinError && <FieldError message={pinError} />}
            </div>

            {generalError && <div className="ck-general-error">{generalError}</div>}

            <button type="submit" disabled={!canSubmit} className="ck-submit">
              {submitting ? 'Pairing...' : 'Checkout'}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}



